import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { HeaderComponent } from '@shared/layout/header/header.component';
import { ComponentsModule, DialogService, SnackBarService, TabAppearance, TabItem } from '@mastechnicaegroup/carelinelive-ui';
import { ActivatedRoute } from '@angular/router';
import { RefreshableResult, successfulResult, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { BehaviorSubject, catchError, map, of, Subject, switchMap, take, tap } from 'rxjs';
import { RegionService } from '@lib/region/region.service';
import { AsyncPipe } from '@angular/common';
import { RegionGeneralInformationComponent } from './accordion/general-information/region-general-information.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RegionPrioritiesTabComponent } from './tabs/priorities/region-priorities-tab.component';
import { RegionZonesTabComponent } from './tabs/zones/region-zones-tab.component';
import { RegionProvidersTabComponent } from './tabs/providers/region-providers-tab.component';
import { RegionAuditTabComponent } from './tabs/audit/region-audit-tab.component';
import { AddRegionZoneDialogComponent } from '../../dialogs/add/zone/add-region-zone-dialog.component';
import { Zone as ZoneData } from '@lib/api/models/zone';
import { EditRegionZoneDialogComponent } from '../../dialogs/edit/zone/edit-region-zone-dialog.component';
import { Region } from '@lib/api/models/region';
import { Priority } from '@lib/api/models/priorities';
import { EditRegionPriorityDialogComponent } from '../../dialogs/edit/priority/edit-region-priority-dialog.component';
import { AddRegionPriorityDialogComponent } from '../../dialogs/add/priority/add-region-priority-dialog.component';
import { PromptRegionStatusChangeDialogComponent } from '../../dialogs/prompt/region/prompt-region-status-change-dialog.component';
import { EditRegionGeneralInformationDialogComponent } from '../../dialogs/edit/general-information/edit-region-general-information-dialog.component';
import { AuthenticationService } from '@lib/auth/authentication.service';

@Component({
    selector: 'app-regions-view-page',
    imports: [HeaderComponent, ComponentsModule, RegionGeneralInformationComponent, AsyncPipe, RegionPrioritiesTabComponent, RegionZonesTabComponent, RegionProvidersTabComponent, RegionAuditTabComponent],
    templateUrl: './regions-view-page.component.html',
    standalone: true,
    styleUrl: './regions-view-page.component.scss'
})
export class RegionsViewPageComponent implements OnInit {

    private readonly _activatedRoute = inject(ActivatedRoute);
    private readonly _authService = inject(AuthenticationService);
    private readonly _regionService = inject(RegionService);
    private readonly _dialog = inject(DialogService);
    private readonly _snackBar = inject(SnackBarService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly refreshTrigger$ = new BehaviorSubject<void>(undefined);
    protected user = toSignal(this._authService.user$);

    readonly auditRefresh$ = new Subject<void>();


    protected currentTabId = '1';
    protected isPrioritiesTabOpen = true;
    protected isZonesTabOpen = false;
    protected isProvidersTabOpen = false;
    protected isAuditTabOpen = false;

    tabAppearance: TabAppearance = 'contained';
    protected readonly tabItems: TabItem[] = [
        {
            disabled: false,
            id: '1',
            title: 'Priorities'
        },
        {
            disabled: false,
            id: '2',
            title: 'Zones'
        },
    ];

    region = new RefreshableResult({
        source: this.refreshTrigger$.pipe(
            switchMap(() => this._activatedRoute.params.pipe(
                map(({ id }) => id),
                switchMap(id => this._regionService.getRegion(id).pipe(
                    toResult()
                ))
            ))
        )
    });

    region$ = this.region.result$.pipe(
        successfulResult()
    );


    ngOnInit() {
        if(this.user()?.permissions?.permission_name.toLowerCase() === 'administrator') {
            this.tabItems.push({
                disabled: false,
                id: '3',
                title: 'Providers'
            });
        }

        this.tabItems.push({
            disabled: false,
            id: '4',
            title: 'Audit'
        });
    }

    addZoneEvent() {
        this.region$.pipe(
            take(1),
            switchMap((regionResult) => {
                const addRegionData = {
                    id: regionResult.data.id,
                    name: regionResult?.data.name
                };

                return AddRegionZoneDialogComponent.show(this._dialog, addRegionData).closed.pipe(
                    tap(() => {
                        this.region.refresh();
                        this.auditRefresh$.next();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            dismissable: false,
                            icon: 'error_outline',
                            title: 'Zone failed to add',
                            actions: [
                                { id: 'undo', label: 'OK' }
                            ]
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    editZoneEvent(zone: ZoneData) {
        this.region$.pipe(
            take(1),
            switchMap((regionResult) => {
                const addRegionData = {
                    id: regionResult.data.id,
                    name: regionResult?.data.name
                };

                const data = {
                    region: {
                        ...addRegionData
                    },
                    zone: {
                        ...zone
                    }
                };

                return EditRegionZoneDialogComponent.show(this._dialog, data).closed.pipe(
                    tap(() => {
                        this.region.refresh();
                        this.auditRefresh$.next();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            dismissable: false,
                            icon: 'error_outline',
                            title: 'Failed to edit zone',
                            actions: [
                                { id: 'undo', label: 'OK' }
                            ]
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    addPriorityEvent() {
        this.region$.pipe(
            take(1),
            switchMap((regionResult) => {
                const addRegionData = {
                    id: regionResult.data.id,
                    name: regionResult?.data.name,
                    priorities: regionResult?.data.priorities.sort((a, b) => a.delay - b.delay)
                };

                return AddRegionPriorityDialogComponent.show(this._dialog, addRegionData).closed.pipe(
                    tap(() => {
                        this.region.refresh();
                        this.auditRefresh$.next();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            dismissable: false,
                            icon: 'error_outline',
                            title: 'Failed to add priority',
                            actions: [
                                { id: 'undo', label: 'OK' }
                            ]
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    editPriorityEvent(priority: Priority) {
        this.region$.pipe(
            take(1),
            switchMap((regionResult) => {
                const addRegionData = {
                    id: regionResult.data.id,
                    name: regionResult?.data.name
                };

                const data = {
                    region: {
                        ...addRegionData
                    },
                    priority: {
                        ...priority
                    }
                };

                return EditRegionPriorityDialogComponent.show(this._dialog, data).closed.pipe(
                    tap(() => {
                        this.region.refresh();
                        this.auditRefresh$.next();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            dismissable: false,
                            icon: 'error_outline',
                            title: 'Failed to edit priority',
                            actions: [
                                { id: 'undo', label: 'OK' }
                            ]
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    changeStatus(region: Region, status: boolean) {
        const regionData = {
            ...region,
            status: status
        };

        return PromptRegionStatusChangeDialogComponent.show(this._dialog, regionData).closed.pipe(
            tap(() => {
                this.refreshTrigger$.next();
            }),
            catchError(() => {
                this._snackBar.open({
                    colour: 'error',
                    dismissable: false,
                    icon: 'error_outline',
                    title: 'Failed to change status for region',
                    actions: [
                        { id: 'undo', label: 'OK' }
                    ]
                });
                return of(null);
            })
        ).subscribe();
    }

    editRegionDetails() {
        this.region$.pipe(
            take(1),
            switchMap((regionResult) => {
                return EditRegionGeneralInformationDialogComponent.show(this._dialog, regionResult.data).closed.pipe(
                    tap(() => {
                        this.region.refresh();
                        this.auditRefresh$.next();
                    }),
                    catchError(() => {
                        this._snackBar.open({
                            colour: 'error',
                            dismissable: false,
                            icon: 'error_outline',
                            title: 'Failed to edit region',
                            actions: [
                                { id: 'undo', label: 'OK' }
                            ]
                        });
                        return of(null);
                    })
                );
            }),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe();
    }

    closeAllTabs() {
        this.isPrioritiesTabOpen = false;
        this.isZonesTabOpen = false;
        this.isProvidersTabOpen = false;
        this.isAuditTabOpen = false;
    }

    openPrioritiesTab() {
        this.closeAllTabs();
        this.isPrioritiesTabOpen = true;
    }

    openZonesTab() {
        this.closeAllTabs();
        this.isZonesTabOpen = true;
    }

    openProvidersTab() {
        this.closeAllTabs();
        this.isProvidersTabOpen = true;
    }

    openAuditTab() {
        this.closeAllTabs();
        this.isAuditTabOpen = true;
    }

    public currentTabIdChange(tabId: string) {
        this.currentTabId = tabId;
        switch (tabId) {
            case '1':
                this.openPrioritiesTab();
                break;
            case '2':
                this.openZonesTab();
                break;
            case '3':
                this.openProvidersTab();
                break;
            case '4':
                this.openAuditTab();
                break;
        }
    }
}
